// Payabli embedded-component loader. Unlike Stripe there is no npm package for
// the browser SDK — Payabli ships an evergreen CDN script that registers a
// global `PayabliComponent` constructor. We inject the script once and resolve
// the constructor, mirroring the singleton pattern in `stripe.ts`.
//
// Sandbox uses a distinct host + a `data-test` attribute; production drops it.
// The environment is driven by the tenant's Payabli mode (TEST/LIVE) surfaced
// by GET /api/settings/payments/payabli-client-config — NOT a build-time flag —
// so a single build serves both.

const SANDBOX_URL = 'https://embedded-component-sandbox.payabli.com/component.js';
const PRODUCTION_URL = 'https://embedded-component.payabli.com/component.js';

export type PayabliMode = 'TEST' | 'LIVE';

/** The rails the embedded component can render. It mounts exactly ONE at a time. */
export type PayabliRail = 'card' | 'ach';

/**
 * Every rail the component can open on: the two a payer types into, plus
 * `rdc` — remote deposit capture, a paper check photographed front and back
 * and then debited by the component itself (`payabliExec('pay')`). Kept apart
 * from `PayabliRail` because nothing that switches a payer between card and
 * bank, or prices a fee off that switch, can ever be on a scanned check.
 */
export type PayabliEmbeddedRail = PayabliRail | 'rdc';

/**
 * The rail a form opens on. The embedded component mounts ONE rail per
 * instance, so this is both the initial tab and — until the payer switches —
 * the rail every fee disclosure on the hosting page must describe.
 */
export function defaultPayabliRail(methods?: { card?: boolean; ach?: boolean }): PayabliRail {
  return (methods?.card ?? true) ? 'card' : 'ach';
}

/**
 * True when `origin` belongs to Payabli's embedded-component iframe.
 *
 * The bundle posts messages to the host page as its iframe document boots
 * (`mounted-component`, `set-token`, `resize`) — the ONLY signal we get that
 * the form actually rendered, because `functionCallBackReady` is a per-field
 * validation callback that never fires until the payer types. We match the
 * whole `payabli.com` suffix rather than one exact host so a future component
 * subdomain can't silently turn every form into a permanent spinner; nothing
 * in the payload is trusted, only the fact that a message arrived.
 */
export function isPayabliComponentOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:') return false;
    return url.hostname === 'payabli.com' || url.hostname.endsWith('.payabli.com');
  } catch {
    return false;
  }
}

export type PayabliComponentSuccess = {
  responseText?: string;
  resultText?: string;
  resultCode?: number | string;
  /**
   * Temporary payment token (temporaryToken: true mode) — the value our
   * backend exchanges for a permanent stored method via TokenStorage/add.
   */
  referenceId?: string;
  methodReferenceId?: string;
  customerId?: number | string;
  // Card metadata surfaced by the component. Nesting varies by version, so
  // consumers read defensively (see extractResult in the setup form).
  responseData?: Record<string, unknown>;
  [key: string]: unknown;
};

/** The component's config minus the open-ended extras it also accepts. */
export type PayabliComponentConfigBase = {
  type: 'methodEmbedded' | 'methodLightbox' | 'vterminal' | 'expressCheckout';
  rootContainer: string;
  token: string;
  entryPoint: string;
  defaultOpen?: PayabliEmbeddedRail;
  temporaryToken?: boolean;
  card?: { enabled?: boolean } & Record<string, unknown>;
  ach?: { enabled?: boolean } & Record<string, unknown>;
  /** Remote deposit capture: `amount` is what the check is to be debited for. */
  rdc?: { enabled?: boolean; amount?: number } & Record<string, unknown>;
  customerData?: Record<string, unknown>;
  functionCallBackReady?: (data: unknown) => void;
  functionCallBackSuccess?: (result: PayabliComponentSuccess) => void;
  functionCallBackError?: (error: unknown) => void;
};

export type PayabliComponentConfig = PayabliComponentConfigBase & Record<string, unknown>;

export type PayabliComponentInstance = {
  show?: () => void;
  hide?: () => void;
  updateConfig?: (config: Partial<PayabliComponentConfig>) => void;
  /**
   * `method` tokenizes without charging; `pay` charges what the form holds —
   * for a scanned check, `{ paymentDetails: { totalAmount, serviceFee: 0 } }`,
   * and the component calls `MoneyIn/getpaid` itself.
   */
  payabliExec: (
    action: 'method' | 'pay' | 'auth' | 'reinit',
    params?: Record<string, unknown>,
  ) => void;
};

export type PayabliComponentCtor = new (config: PayabliComponentConfig) => PayabliComponentInstance;

// The loader resolves per mode; sandbox and production ship distinct globals,
// so we cache one promise per mode rather than a single global.
const loadPromises: Partial<Record<PayabliMode, Promise<PayabliComponentCtor | null>>> = {};

// The Payabli CDN script declares `class PayabliComponent {}` at the top level
// of a *classic* script — a binding in the global lexical environment. Per
// spec, top-level `class` (like `let`/`const`) is reachable as a bare
// identifier (resolved via the global lexical environment) but is NOT a
// property of `window`/`globalThis` — so `window.PayabliComponent` is always
// `undefined`. Ambient-declare it so TypeScript knows the global exists; a bare
// reference then resolves the binding at runtime with NO `eval` (no
// `unsafe-eval`) and NO inline script (no `unsafe-inline`) — CSP-clean.
declare const PayabliComponent: PayabliComponentCtor | undefined;

/** Resolve the `PayabliComponent` constructor the loaded CDN script exposes. */
function resolvePayabliCtor(): PayabliComponentCtor | null {
  return typeof PayabliComponent !== 'undefined' ? PayabliComponent : null;
}

/**
 * Inject the Payabli component script (once per mode) and resolve the
 * `PayabliComponent` constructor. Resolves `null` when the script fails to
 * load, when running without a DOM (SSR), or when a DIFFERENT environment's
 * script is already loaded (see below), so callers render a graceful error.
 *
 * Payabli exposes a single global `PayabliComponent`, so a page can only host
 * ONE environment's script. If a script for another mode is already present
 * (e.g. a tenant switch flips TEST↔LIVE mid-session), we refuse rather than
 * hand back the wrong-environment constructor — the page must reload to switch.
 */
export function loadPayabliComponent(mode: PayabliMode): Promise<PayabliComponentCtor | null> {
  const cached = loadPromises[mode];
  if (cached) return cached;

  // `fail()` closes over `promise` so it only ever evicts ITS OWN cache entry
  // (a later mount may already have installed a fresh one). The closure runs
  // from the script's async load/error listeners, long after the binding is
  // initialized.
  const promise: Promise<PayabliComponentCtor | null> = new Promise((resolve) => {
    /**
     * A failed load must NOT be remembered. Caching the rejected promise (and
     * leaving the dead <script> in <head>) bricked every later Payabli mount in
     * the SPA session — one transient CDN blip and the payer got "failed to
     * load" on every pay page until a full reload, with no retry path.
     */
    const fail = (deadScript?: HTMLScriptElement | null) => {
      if (loadPromises[mode] === promise) delete loadPromises[mode];
      deadScript?.remove();
      resolve(null);
    };

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve(null);
      return;
    }

    const sandbox = mode !== 'LIVE';
    const url = sandbox ? SANDBOX_URL : PRODUCTION_URL;
    // `load` firing is not success: a truncated or blocked bundle loads and
    // registers no `PayabliComponent`. Resolving `null` from a CACHED promise
    // there was the same trap as a network failure — the form showed its load
    // error and `Try again` got the identical cached `null` forever. A missing
    // constructor after `load` is a failed load, so treat it as one.
    const finish = (script: HTMLScriptElement | null) => () => {
      const ctor = resolvePayabliCtor();
      if (ctor) resolve(ctor);
      else fail(script);
    };

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-payabli-component="true"]',
    );

    if (existing) {
      const existingMode = existing.getAttribute('data-payabli-mode');
      if (existingMode && existingMode !== mode) {
        // A different environment is already loaded; the single global
        // `PayabliComponent` can't be swapped without a reload. Refuse rather
        // than render a sandbox component for a LIVE tenant (or vice versa).
        console.error(
          `Payabli component already loaded for mode "${existingMode}"; cannot switch to "${mode}" without a page reload.`,
        );
        // Cached on purpose, unlike a transient load failure: the wrong-mode
        // global can only be cleared by a page reload, so retrying inside this
        // session would refuse again. The other mode's script stays — it is
        // the one in use.
        resolve(null);
        return;
      }
      const already = resolvePayabliCtor();
      if (already) resolve(already);
      else {
        existing.addEventListener('load', finish(existing), { once: true });
        existing.addEventListener('error', () => fail(existing), { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.setAttribute('data-payabli-component', 'true');
    script.setAttribute('data-payabli-mode', mode);
    // Sandbox library requires the `data-test` attribute on its script tag.
    if (sandbox) script.setAttribute('data-test', '');
    script.addEventListener('load', finish(script), { once: true });
    script.addEventListener('error', () => fail(script), { once: true });
    document.head.appendChild(script);
  });

  loadPromises[mode] = promise;
  return promise;
}

function trimmedText(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

/**
 * The component's own form refusal: a list of `{ code, key, text }` entries,
 * one per field it would not submit (the sandbox sends
 * `{ code: 907, key: 'paymentMethodsRdcAccountHolderNameError', text: … }` for
 * an empty NAME ON CHECK). It is raised in the browser before the component
 * sends anything to Payabli, so no debit can be behind it. Null for anything
 * else — every other error during a debit may still have one behind it.
 */
export function componentFieldErrors(error: unknown): string[] | null {
  if (!Array.isArray(error) || error.length === 0) return null;
  const texts: string[] = [];
  for (const entry of error) {
    const record = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : null;
    const key = trimmedText(record?.key);
    const text = trimmedText(record?.text);
    if (!key || !text || !/^paymentMethods\w*Error$/.test(key)) return null;
    texts.push(text);
  }
  return texts;
}

/**
 * Payabli's servers refusing the payment method after the form passed its own
 * checks: the component wraps the reason as `[[{ code, key: 'apiError', text }]]`,
 * a list inside a list. The reason Payabli gave, or null.
 */
export function componentApiErrorText(error: unknown): string | null {
  if (!Array.isArray(error)) return null;
  for (const entry of error.flat()) {
    const record = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : null;
    if (record?.key !== 'apiError') continue;
    const text = trimmedText(record.text);
    if (text) return text;
  }
  return null;
}

/**
 * What a refusal was, safe to log: each entry's `key` and `code`, and a bare
 * string code as is. Never the entries' text or the payload itself: a server
 * refusal can carry Payabli's whole response, and the form is on public pages
 * whose console can end up in error reports.
 */
export function payabliRefusalSummary(error: unknown): { keys: string[]; codes: string[] } {
  const keys: string[] = [];
  const codes: string[] = [];
  const entries = Array.isArray(error) ? error.flat() : [error];
  for (const entry of entries) {
    if (typeof entry === 'string') {
      if (/^\w{1,64}$/.test(entry)) codes.push(entry);
      continue;
    }
    const record = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : null;
    const key = record?.key;
    const code = record?.code;
    if (typeof key === 'string' && /^\w{1,64}$/.test(key)) keys.push(key);
    if (typeof code === 'string' || typeof code === 'number') codes.push(String(code));
  }
  return { keys, codes };
}

/**
 * What to tell the payer when the component refuses to save a payment method.
 *
 * Customers see this (the portal, pay links, the card-repair page), so it is
 * never Payabli's own wording for anything but a field: its field messages
 * are already plain ("Your card number is invalid"), but its server answers
 * speak of paypoints, entry points and tokens, and a code like
 * `customerError` means nothing to anyone. A reason about the card or account
 * becomes a plain sentence about that field; anything else is our setup, not
 * the payer's mistake, and reads as a retry. Nothing here names Payabli.
 */
export function payabliRefusalMessage(error: unknown, rail: PayabliRail): string {
  const fieldErrors = componentFieldErrors(error);
  if (fieldErrors) {
    return fieldErrors.map((text) => (/[.!?]$/.test(text) ? text : `${text}.`)).join(' ');
  }
  const card = rail === 'card';
  const retry = card
    ? "We couldn't save your card. Please try again, or use a different card."
    : "We couldn't save your bank account. Please try again, or use a different account.";
  const reason = componentApiErrorText(error) ?? '';
  // Our setup, not the payer's details: "token expired" is no card expiry,
  // and "invalid account for this paypoint" is no bad account number.
  if (/paypoint|entry ?point|token|api ?key|merchant|configur|internal|server|timeout/i.test(reason)) {
    return retry;
  }
  if (/declin|do not honor|insufficient|not permitted|restricted|pick ?up|lost|stolen|fraud/i.test(reason)) {
    return card
      ? 'Your card was declined. Try a different card or contact your bank.'
      : 'Your bank declined this account. Try a different account or contact your bank.';
  }
  if (/card.*expir|expir.*(date|card)/i.test(reason)) return "Your card's expiration date is invalid or the card has expired.";
  if (/cvv|cvc|security code/i.test(reason)) return "Your card's security code is incorrect.";
  if (/zip|postal|avs/i.test(reason)) return "Your card's zip code doesn't match the card.";
  if (/routing|\baba\b/i.test(reason)) return 'Your routing number is invalid.';
  if (/card ?number|invalid card|luhn/i.test(reason)) return 'Your card number is invalid.';
  if (/account ?number|invalid account|account.*closed/i.test(reason)) {
    return 'Your account number is invalid.';
  }
  return retry;
}

/** What the component said went wrong, without echoing a payload wholesale. */
export function componentErrorText(error: unknown, fallback: string): string {
  if (typeof error === 'string' && error.trim()) return error.trim();
  const fieldErrors = componentFieldErrors(error);
  if (fieldErrors) return fieldErrors.join('. ');
  if (Array.isArray(error)) {
    const codes = error.filter((e): e is string => typeof e === 'string');
    if (codes.length) return `${fallback} (${codes.join(', ')})`;
  }
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const direct = trimmedText(record.responseText) ?? trimmedText(record.resultText);
    if (direct) return direct;
    const errors = Array.isArray(record.errors) ? record.errors : [];
    const first = errors
      .map((e) => (typeof e === 'string' ? e : trimmedText((e as Record<string, unknown> | null)?.message)))
      .find((e): e is string => Boolean(e));
    if (first) return first;
  }
  return fallback;
}
